import logger from '@middleware/logger';
import { TrackerAPIClient } from '@services/axios/clients/trackerAPIClient';
import {
  CreateSelectorRequest,
  UpdateSelectorRequest,
  SelectorResponse,
  ProjectResponse,
} from '@shared/interfaces/api/trackerRequest';

const isAxiosError = (error: unknown): error is any => {
  return error && typeof error === 'object' && 'response' in error;
};

export const getProjectSelectors = async (projectSlug: string) => {
  try {
    const response = await TrackerAPIClient.get<SelectorResponse[]>(
      `/projects/${projectSlug}/selectors`,
    );

    return { data: response.data, error: null };
  } catch (error: unknown) {
    logger.error(error);

    let message;

    if (isAxiosError(error)) {
      message = (error.response?.data as any)?.message;
    }

    return {
      data: null,
      error: message || "Couldn't fetch selectors. The server didn't respond!",
    };
  }
};

export const createSelector = async (
  projectSlug: string,
  data: CreateSelectorRequest,
) => {
  try {
    const response = await TrackerAPIClient.post<SelectorResponse>(
      `/projects/${projectSlug}/selectors`,
      data,
    );

    return { data: response.data, error: null };
  } catch (error: unknown) {
    logger.error(error);

    let message;

    if (isAxiosError(error)) {
      message = error.response?.data?.message;
    }

    return {
      data: null,
      error: message || "Couldn't create selector. The server didn't respond!",
    };
  }
};

export const updateSelector = async (
  selectorId: number,
  data: UpdateSelectorRequest,
) => {
  try {
    const response = await TrackerAPIClient.patch<SelectorResponse>(
      `/selectors/${selectorId}`,
      data,
    );

    return { data: response.data, error: null };
  } catch (error: unknown) {
    logger.error(error);

    let message;

    if (isAxiosError(error)) {
      message = error.response?.data?.message;
    }

    return {
      data: null,
      error: message || "Couldn't update selector. The server didn't respond!",
    };
  }
};

export const deleteSelector = async (selectorId: number) => {
  try {
    const response = await TrackerAPIClient.delete<{ message: string }>(
      `/selectors/${selectorId}`,
    );

    return { data: response.data, error: null };
  } catch (error: unknown) {
    logger.error(error);

    let message;

    if (isAxiosError(error)) {
      message = error.response?.data?.message;
    }

    return {
      data: null,
      error: message || "Couldn't delete selector. The server didn't respond!",
    };
  }
};

export const regenerateTracker = async (projectSlug: string) => {
  try {
    const response = await TrackerAPIClient.post<ProjectResponse>(
      `/projects/${projectSlug}/regenerate`,
    );

    return { data: response.data, error: null };
  } catch (error: unknown) {
    logger.error(error);

    let message;

    if (isAxiosError(error)) {
      message = error.response?.data?.message;
    }

    return {
      data: null,
      error: message || "Couldn't regenerate tracker. The server didn't respond!",
    };
  }
};

export const getProject = async (projectSlug: string) => {
  try {
    const response = await TrackerAPIClient.get<ProjectResponse>(
      `/projects/${projectSlug}`,
    );

    return { data: response.data, error: null };
  } catch (error: unknown) {
    logger.error(error);

    let message;

    if (isAxiosError(error)) {
      message = error.response?.data?.message;
    }

    return {
      data: null,
      error: message || "Couldn't fetch project. The server didn't respond!",
    };
  }
};

export const updateProject = async (
  projectSlug: string,
  data: Partial<Pick<ProjectResponse, 'track_all_links' | 'track_all_buttons' | 'name' | 'description' | 'is_active'>>,
) => {
  try {
    const response = await TrackerAPIClient.patch<ProjectResponse>(
      `/projects/${projectSlug}`,
      data,
    );

    return { data: response.data, error: null };
  } catch (error: unknown) {
    logger.error(error);

    let message;

    if (isAxiosError(error)) {
      message = error.response?.data?.message;
    }

    return {
      data: null,
      error: message || "Couldn't update project. The server didn't respond!",
    };
  }
};
